import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Calendar, MessageCircle, BarChart, 
  Globe, ChevronRight, Mail, Smartphone, 
  Layers, PanelRight, MessageSquare, FileCode,
  Bot, BrainCircuit, Monitor, Code, Zap,
  Building, ArrowRight, LineChart, LayoutGrid,
  CheckSquare, PlusCircle, Download, MessagesSquare,
  Clock, Star, Share2, Shield, BookOpen, Headphones,
  Phone, Mic, Volume2
} from 'lucide-react';
import SectionTransitionEffect from '../components/ui/SectionTransitionEffect';
import { Link } from 'react-router-dom';
import SiteMetadata from '../components/SEO/SiteMetadata';

const FeaturesPage: React.FC = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1.0] }
    }
  };

  // Features by category
  const featureCategories = [
    {
      id: "communication",
      title: "Communication",
      description: "Keep in touch with clients through powerful messaging systems",
      features: [
        {
          icon: <MessagesSquare className="text-accent" size={28} />,
          title: "Mass Text Messaging",
          description: "Send personalized text messages to your entire client base or targeted segments with just a few clicks.",
          mockup: (
            <div className="glassmorphic rounded-xl p-4">
              <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                <div className="text-lg font-semibold">Text Campaign Builder</div>
                <div className="flex space-x-2">
                  <button className="bg-white/10 p-1.5 rounded">
                    <PlusCircle size={16} />
                  </button>
                  <button className="bg-white/10 p-1.5 rounded">
                    <Download size={16} />
                  </button>
                </div>
              </div>
              
              <div className="flex flex-col space-y-3">
                <div className="flex justify-between items-center">
                  <div className="text-sm">Recipients</div>
                  <div className="bg-white/5 rounded px-2 py-1 text-xs">294 contacts</div>
                </div>
                
                <div className="bg-white/5 p-3 rounded-lg text-sm border border-white/10 min-h-[80px]">
                  Hi {"{first_name}"}, this is {"{business_name}"}! We're offering 20% off ceramic coatings this month. Reply YES to schedule your appointment!
                </div>
                
                <div className="flex justify-between text-xs text-white/60">
                  <div>0/160 characters</div>
                  <div>Estimated delivery: 3min</div>
                </div>
                
                <div className="flex justify-between pt-2">
                  <button className="bg-white/10 hover:bg-white/20 py-1.5 px-3 rounded text-sm">Add Media</button>
                  <button className="bg-accent text-background py-1.5 px-3 rounded text-sm font-medium">Send Campaign</button>
                </div>
              </div>
            </div>
          )
        },
        {
          icon: <Mail className="text-accent" size={28} />,
          title: "Mass Email Campaigns",
          description: "Create and send beautiful email campaigns to promote your services and stay top of mind with clients.",
          mockup: (
            <div className="glassmorphic rounded-xl p-4">
              <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                <div className="text-lg font-semibold">Email Designer</div>
                <div className="flex space-x-2">
                  <button className="bg-white/10 p-1.5 rounded">
                    <CheckSquare size={16} />
                  </button>
                  <button className="bg-white/10 p-1.5 rounded">
                    <Download size={16} />
                  </button>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-lg p-3 border border-white/10 mb-3">
                <div className="bg-white/10 h-5 w-1/2 rounded mb-2"></div>
                <div className="bg-white/10 h-3 w-3/4 rounded mb-1"></div>
                <div className="bg-white/10 h-3 w-2/3 rounded mb-1"></div>
                <div className="bg-white/10 h-3 w-3/4 rounded mb-3"></div>
                <div className="bg-accent/20 h-8 text-accent text-xs flex items-center justify-center rounded mb-2">View Our Spring Specials</div>
                <div className="bg-white/10 h-24 rounded flex items-center justify-center">
                  <Monitor size={20} className="text-white/30" />
                </div>
              </div>
              
              <div className="flex justify-between pt-1">
                <button className="bg-white/10 hover:bg-white/20 py-1.5 px-3 rounded text-sm">Add Section</button>
                <button className="bg-accent text-background py-1.5 px-3 rounded text-sm font-medium">Send Campaign</button>
              </div>
            </div>
          )
        },
        {
          icon: <Layers className="text-accent" size={28} />,
          title: "Automated Email Drip",
          description: "Set up automated email sequences that nurture leads and maintain client relationships on autopilot.",
          mockup: (
            <div className="glassmorphic rounded-xl p-4">
              <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                <div className="text-lg font-semibold">Drip Campaign Flow</div>
                <div className="flex space-x-2">
                  <button className="bg-white/10 p-1.5 rounded">
                    <LayoutGrid size={16} />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-accent/20 text-accent flex items-center justify-center flex-shrink-0">1</div>
                  <div className="h-px w-8 bg-accent/40"></div>
                  <div className="bg-white/10 rounded p-2 flex-1 text-xs">Welcome Email (Day 0)</div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">2</div>
                  <div className="h-px w-8 bg-white/20"></div>
                  <div className="bg-white/5 rounded p-2 flex-1 text-xs">Service Overview (Day 3)</div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">3</div>
                  <div className="h-px w-8 bg-white/20"></div>
                  <div className="bg-white/5 rounded p-2 flex-1 text-xs">Testimonials (Day 7)</div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">4</div>
                  <div className="h-px w-8 bg-white/20"></div>
                  <div className="bg-white/5 rounded p-2 flex-1 text-xs">Special Offer (Day 14)</div>
                </div>
              </div>
            </div>
          )
        }
      ]
    },
    {
      id: "workflow",
      title: "Workflow Automation",
      description: "Streamline your business processes with powerful automation tools",
      features: [
        {
          icon: <Zap className="text-accent" size={28} />,
          title: "Workflow Builder",
          description: "Create custom workflows to automate repetitive tasks and business processes without coding.",
          mockup: (
            <div className="glassmorphic rounded-xl p-4">
              <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                <div className="text-lg font-semibold">Workflow Builder</div>
                <div className="flex space-x-2">
                  <button className="bg-white/10 p-1.5 rounded">
                    <Code size={16} />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="w-10 h-10 rounded bg-accent/20 text-accent flex items-center justify-center flex-shrink-0 mt-3">
                    <Calendar size={18} />
                  </div>
                  <div className="h-6 w-6 border-l-2 border-t-2 border-white/20 ml-5 -mt-1"></div>
                  <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center flex-shrink-0 mt-3">
                    <Mail size={18} />
                  </div>
                  <div className="h-6 w-6 border-l-2 border-t-2 border-white/20 ml-5 -mt-1"></div>
                  <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center flex-shrink-0 mt-3">
                    <Smartphone size={18} />
                  </div>
                </div>
                
                <div className="flex gap-2 bg-white/5 p-2 rounded text-xs">
                  <div className="text-accent">TRIGGER:</div>
                  <div>When appointment is scheduled</div>
                </div>
                
                <div className="flex gap-2 bg-white/5 p-2 rounded text-xs">
                  <div className="text-accent">ACTION 1:</div>
                  <div>Send confirmation email with appointment details</div>
                </div>
                
                <div className="flex gap-2 bg-white/5 p-2 rounded text-xs">
                  <div className="text-accent">ACTION 2:</div>
                  <div>Send SMS reminder 24 hours before appointment</div>
                </div>
              </div>
            </div>
          )
        },
        {
          icon: <FileCode className="text-accent" size={28} />,
          title: "Landing Page Builder",
          description: "Create high-converting landing pages for your services and promotions without design skills.",
          mockup: (
            <div className="glassmorphic rounded-xl p-4">
              <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                <div className="text-lg font-semibold">Page Builder</div>
                <div className="flex space-x-2">
                  <button className="bg-white/10 p-1.5 rounded">
                    <Monitor size={16} />
                  </button>
                </div>
              </div>
              
              <div className="bg-white/5 rounded-lg border border-white/10 p-3">
                <div className="flex items-center justify-between text-xs mb-2">
                  <div className="bg-white/10 w-20 h-5 rounded"></div>
                  <div className="flex space-x-1">
                    <div className="bg-white/10 w-12 h-5 rounded"></div>
                    <div className="bg-white/10 w-12 h-5 rounded"></div>
                    <div className="bg-white/10 w-12 h-5 rounded"></div>
                  </div>
                </div>
                
                <div className="bg-white/10 w-3/4 h-8 rounded mb-2 mx-auto"></div>
                <div className="bg-white/10 w-1/2 h-4 rounded mb-3 mx-auto"></div>
                
                <div className="flex justify-center gap-2 mb-3">
                  <div className="bg-accent w-24 h-6 rounded"></div>
                  <div className="bg-white/20 w-24 h-6 rounded"></div>
                </div>
                
                <div className="bg-white/10 w-full h-32 rounded"></div>
              </div>
              
              <div className="flex justify-between mt-3 pt-1">
                <div className="text-xs text-white/60">Drag & drop elements</div>
                <button className="bg-accent text-background py-1 px-2 rounded text-xs font-medium">Publish</button>
              </div>
            </div>
          )
        },
        {
          icon: <LineChart className="text-accent" size={28} />,
          title: "Marketing Channel Source Tracking",
          description: "Track which marketing channels are bringing in leads and customers to optimize your marketing spend.",
          mockup: (
            <div className="glassmorphic rounded-xl p-4">
              <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-3">
                <div className="text-lg font-semibold">Source Analytics</div>
                <div className="flex space-x-2">
                  <button className="bg-white/10 p-1.5 rounded">
                    <BarChart size={16} />
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-xs text-white/70 px-1">
                  <span>Marketing Source</span>
                  <span>Conversion Rate</span>
                </div>
                
                <div className="flex items-center justify-between bg-white/5 p-2 rounded">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center">
                      <Globe size={14} />
                    </div>
                    <div className="text-sm">Google Ads</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-3 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-green-500 w-[65%]"></div>
                    </div>
                    <div className="text-sm">65%</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between bg-white/5 p-2 rounded">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <MessageSquare size={14} />
                    </div>
                    <div className="text-sm">Facebook</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-3 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-blue-500 w-[48%]"></div>
                    </div>
                    <div className="text-sm">48%</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between bg-white/5 p-2 rounded">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <Mail size={14} />
                    </div>
                    <div className="text-sm">Email</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-3 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full bg-purple-500 w-[72%]"></div>
                    </div>
                    <div className="text-sm">72%</div>
                  </div>
                </div>
              </div>
            </div>
          )
        }
      ]
    }
  ];
  
  // Integrations
  const integrations = [
    {
      name: "Urable",
      description: "Leading CRM & business management platform for detailers",
      logo: "https://images.pexels.com/photos/4792733/pexels-photo-4792733.jpeg?auto=compress&cs=tinysrgb&w=60"
    },
    {
      name: "TintWiz",
      description: "Specialized software for window tint shops",
      logo: "https://images.pexels.com/photos/4792729/pexels-photo-4792729.jpeg?auto=compress&cs=tinysrgb&w=60"
    },
    {
      name: "OrbisX",
      description: "Comprehensive solution for auto appearance businesses",
      logo: "https://images.pexels.com/photos/4792731/pexels-photo-4792731.jpeg?auto=compress&cs=tinysrgb&w=60"
    },
    {
      name: "CP Shop Manager",
      description: "Complete management system for auto body shops",
      logo: "https://images.pexels.com/photos/4792727/pexels-photo-4792727.jpeg?auto=compress&cs=tinysrgb&w=60"
    },
    {
      name: "Zapier",
      description: "Connect DetailerOS with 3,000+ apps and services",
      logo: "https://images.pexels.com/photos/4792735/pexels-photo-4792735.jpeg?auto=compress&cs=tinysrgb&w=60"
    }
  ];

  // AI features from CHiP page
  const aiFeatures = [
    {
      icon: <Calendar className="text-accent" size={28} />,
      title: "24/7 Appointment Booking",
      description: "CHiP books appointments automatically based on your availability, handles reschedules, and sends reminders to reduce no-shows."
    },
    {
      icon: <Smartphone className="text-accent" size={28} />,
      title: "Multi-Channel Support",
      description: "Works seamlessly across SMS, website chat, Facebook Messenger, Instagram, WhatsApp, and email."
    },
    {
      icon: <BrainCircuit className="text-accent" size={28} />,
      title: "Industry-Specific Knowledge",
      description: "Pre-trained with deep knowledge of detailing services, paint protection, ceramic coatings, and common customer questions."
    },
    {
      icon: <MessageSquare className="text-accent" size={28} />,
      title: "Natural Conversations",
      description: "Engages in human-like conversations that build rapport and trust with potential customers."
    },
    {
      icon: <Bot className="text-accent" size={28} />,
      title: "Lead Qualification",
      description: "Asks the right questions to qualify leads and prioritize high-value opportunities for your sales team."
    },
    {
      icon: <Clock className="text-accent" size={28} />,
      title: "Time-Sensitive Response",
      description: "Responds within seconds to every inquiry, boosting your conversion rates through immediate engagement."
    },
    {
      icon: <Users className="text-accent" size={28} />,
      title: "Smart Handoff",
      description: "Knows when to transfer complex conversations to your team, with full context provided for seamless transitions."
    },
    {
      icon: <Mic className="text-accent" size={28} />,
      title: "Voice Mode (Coming Soon)",
      description: "Handle phone calls with AI that uses natural-sounding voice to book appointments and answer questions 24/7."
    }
  ];

  return (
    <div className="pt-32 pb-20">
      {/* SEO Metadata */}
      <SiteMetadata 
        page="features"
        title="DetailerOS Features - Comprehensive Tools for Auto Detailers"
        description="Explore the complete toolkit DetailerOS offers to help your detailing business thrive. From client management to communication tools, discover how our features can transform your business."
        keywords={[
          "detailing business tools", 
          "auto detailer software", 
          "detailing CRM", 
          "auto detail marketing tools", 
          "detailing business management software"
        ]}
      />
      
      {/* Background gradient */}
      <motion.div 
        className="absolute rounded-full opacity-60 blur-3xl -z-10 w-[800px] h-[800px] top-0 right-[10%]"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 0.6, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(204, 255, 0, 0.3), rgba(204, 255, 0, 0) 70%)'
        }}
      />
      
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <SectionTransitionEffect>
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <motion.span 
              className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              DetailerOS + GRIT SUITE
            </motion.span>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-6">Powerful Features for Detailing Professionals</h1>
            <p className="text-lg text-white/80 mb-8">
              DetailerOS and GRIT SUITE combine to create the ultimate toolkit for growing and managing your detailing business. 
              Explore our comprehensive features designed specifically for detailing professionals.
            </p>
          </div>
        </SectionTransitionEffect>
        
        {/* Top Features Showcase */}
        <div className="mb-20">
          {featureCategories.map((category, categoryIndex) => (
            <div key={category.id} className="mb-16">
              <SectionTransitionEffect delay={0.2}>
                <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">{category.title}</h2>
                <p className="text-white/70 text-center max-w-3xl mx-auto mb-10">{category.description}</p>
              </SectionTransitionEffect>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                {category.features.map((feature, featureIndex) => (
                  <SectionTransitionEffect key={`${category.id}-${featureIndex}`} delay={0.2 + featureIndex * 0.1}>
                    <div className="flex flex-col h-full">
                      <div className="glassmorphic rounded-xl p-6 mb-6 hover:border-accent/20 transition-colors flex-1">
                        <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                          {feature.icon}
                        </div>
                        <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                        <p className="text-white/70">{feature.description}</p>
                      </div>
                      
                      {/* Feature Mockup */}
                      <div className="flex-1">
                        {feature.mockup}
                      </div>
                    </div>
                  </SectionTransitionEffect>
                ))}
              </div>
              
              {categoryIndex < featureCategories.length - 1 && (
                <div className="border-b border-white/5 w-1/3 mx-auto my-16"></div>
              )}
            </div>
          ))}
        </div>
        
        {/* Integrations Section */}
        <div className="mb-24">
          <SectionTransitionEffect>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                Seamless Connections
              </motion.span>
              <h2 className="text-3xl font-bold mb-6">Integrates With All Major Shop Software</h2>
              <p className="text-white/70 mb-10">
                DetailerOS connects with your favorite tools to create a unified platform for your business.
                No more jumping between different systems!
              </p>
            </div>
          </SectionTransitionEffect>
          
          <div className="glassmorphic rounded-xl p-8 overflow-hidden relative">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-accent via-accent/50 to-transparent"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {integrations.map((integration, index) => (
                <motion.div 
                  key={index}
                  className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-accent/20 rounded-xl p-6 text-center transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="w-16 h-16 mx-auto rounded-full overflow-hidden mb-4 border-2 border-accent/20">
                    <div className="w-full h-full bg-white/10 flex items-center justify-center">
                      <Building className="text-accent" size={24} />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold mb-2">{integration.name}</h3>
                  <p className="text-sm text-white/70 mb-4">{integration.description}</p>
                  <div className="text-accent text-sm font-medium">
                    Connect
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <p className="text-white/70 mb-4">Don't see your tool? We're adding new integrations regularly.</p>
              <motion.button
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Request Integration
              </motion.button>
            </div>
          </div>
        </div>
        
        {/* AI Features */}
        <div className="mb-24">
          <SectionTransitionEffect>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <motion.span 
                className="px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-semibold text-sm inline-block mb-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                AI-Powered Capabilities
              </motion.span>
              <h2 className="text-3xl font-bold mb-6">CHiP - The Industry's Only AI Assistant</h2>
              <p className="text-white/70 mb-10">
                CHiP is the detailing industry's only AI assistant specifically trained to book appointments, 
                qualify leads, and answer detailed questions about automotive services.
              </p>
            </div>
          </SectionTransitionEffect>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiFeatures.map((feature, index) => (
              <motion.div 
                key={index} 
                className="glassmorphic rounded-xl p-6 hover:border-accent/20 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-white/70">{feature.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Link to="/chip">
              <motion.button
                className="btn btn-primary inline-flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Learn More About CHiP</span>
                <ChevronRight size={18} />
              </motion.button>
            </Link>
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="mb-12">
          <SectionTransitionEffect>
            <div className="glassmorphic rounded-xl p-8 md:p-12 relative overflow-hidden border border-accent/20 shadow-glow-sm">
              <div className="absolute -top-24 -right-24 w-48 h-48 rounded-full bg-accent/20 blur-3xl"></div>
              <div className="absolute -bottom-24 -left-24 w-48 h-48 rounded-full bg-accent/20 blur-3xl"></div>
              
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Detailing Business?</h2>
                <p className="text-lg text-white/80 mb-8">
                  Join DetailerOS today and get access to all these powerful features plus expert coaching to help you scale your business.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <motion.a
                    href="#signup"
                    className="btn btn-primary"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Start Your Free Trial
                  </motion.a>
                  <motion.a
                    href="#demo"
                    className="btn btn-secondary"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Schedule a Demo
                  </motion.a>
                </div>
              </div>
            </div>
          </SectionTransitionEffect>
        </div>
      </div>
    </div>
  );
};

export default FeaturesPage;